/**
 * The relay reboot's siege objective (M3 plan R19, D-TBD 12): a damageable ECS entity that is not a ship. It has a
 * float64 position, a team, hull (no shield) and life, plus a `SiegeObjective` weight, so M1's projectiles and
 * torpedoes damage it through `applyDamage` and the AI weighs it as a target (`AiShips.objectives`). The relay's
 * streamed colliders are remapped from `EID_DERELICT` to the objective while it stands, so raider fire that hits the
 * ring damages it; every outcome maps them back. Creating, remapping and removing are rare events (they allocate).
 */
import { addComponent, addEntity, commitRemovals, entityExists, registerQuery, removeEntity, type Query, type World } from 'bitecs'
import { logger } from '../core/logger'
import { EID_DERELICT, type Physics } from '../physics/physics'
import { SiegeObjective } from '../reach/components'
import type { Sim } from '../sim'
import { Hull, Life, MAX_ENTITIES, Pos64, PrevPos64, PrevRot, Rot, Shield, Team, Vel } from '../world/components'

/** A static collider belongs to a structure when its body sits within this distance of the structure's pose. */
export const STRUCTURE_MATCH_M = 1

const OBJECTIVE_COMPONENTS = [Pos64, PrevPos64, Rot, PrevRot, Vel, Team, Hull, Shield, Life, SiegeObjective]

const queries = new WeakMap<World, Query>()

/** The world's registered `[SiegeObjective]` query (registered once per world: bitECS `query()` allocates). */
export function siegeObjectiveQuery(world: World): Query {
  let q = queries.get(world)
  if (q === undefined) {
    q = registerQuery(world, [SiegeObjective])
    queries.set(world, q)
  }
  return q
}

/**
 * Creates a siege objective at the float64 world point (x, y, z): at rest, on `team`, with `hull` points and no
 * shield, weighed at distance / `weight` by the AI. Points `sim.ai.ships.objectives` at the objective query, so the
 * pilots see it from the next tick. Returns the entity id.
 */
export function createSiegeObjective(sim: Sim, x: number, y: number, z: number, team: number, hull: number, weight: number): number {
  const eid = addEntity(sim.world)
  if (eid >= MAX_ENTITIES) {
    removeEntity(sim.world, eid)
    throw new Error(`Entity limit ${MAX_ENTITIES} reached`)
  }
  for (let i = 0; i < OBJECTIVE_COMPONENTS.length; i++) addComponent(sim.world, eid, OBJECTIVE_COMPONENTS[i])
  Pos64.x[eid] = PrevPos64.x[eid] = x
  Pos64.y[eid] = PrevPos64.y[eid] = y
  Pos64.z[eid] = PrevPos64.z[eid] = z
  Rot.x[eid] = PrevRot.x[eid] = 0
  Rot.y[eid] = PrevRot.y[eid] = 0
  Rot.z[eid] = PrevRot.z[eid] = 0
  Rot.w[eid] = PrevRot.w[eid] = 1
  Vel.x[eid] = Vel.y[eid] = Vel.z[eid] = 0
  Team.id[eid] = team
  Hull.value[eid] = hull
  Shield.value[eid] = 0
  Shield.lastHit[eid] = -1e9
  Life.alive[eid] = 1
  Life.respawnAt[eid] = 0
  SiegeObjective.weight[eid] = weight
  sim.ai.ships.objectives = siegeObjectiveQuery(sim.world).dense
  return eid
}

/**
 * Removes a siege objective (a no-op for an entity that is gone). When none is left the AI goes back to ships only
 * (`sim.ai.ships.objectives` unset), which is M1's behaviour exactly.
 */
export function removeSiegeObjective(sim: Sim, eid: number): void {
  if (eid < 0) return
  SiegeObjective.weight[eid] = 0
  Life.alive[eid] = 0
  if (entityExists(sim.world, eid)) removeEntity(sim.world, eid)
  commitRemovals(sim.world)
  if (siegeObjectiveQuery(sim.world).dense.length === 0) sim.ai.ships.objectives = undefined
}

/**
 * Maps every static collider of the structure at the float64 world point (x, y, z) (its fixed body within
 * `STRUCTURE_MATCH_M`, currently mapped to `EID_DERELICT`) to entity `eid`, appending the handles to `out`.
 * Returns how many it mapped.
 */
export function remapStructureColliders(p: Physics, x: number, y: number, z: number, eid: number, out: number[]): number {
  let n = 0
  const tol2 = STRUCTURE_MATCH_M * STRUCTURE_MATCH_M
  for (const [handle, mapped] of p.colliderEid) {
    if (mapped !== EID_DERELICT) continue
    const body = p.world.getCollider(handle)?.parent()
    if (!body) continue
    const t = body.translation()
    const dx = t.x + p.origin.x - x
    const dy = t.y + p.origin.y - y
    const dz = t.z + p.origin.z - z
    if (dx * dx + dy * dy + dz * dz > tol2) continue
    p.colliderEid.set(handle, eid)
    out.push(handle)
    n++
  }
  if (n === 0) logger.debug('relink: no streamed colliders at the relay to remap (not streamed in yet?)')
  return n
}

/** Maps `handles` back to `EID_DERELICT` where they still map to `eid` (a despawned structure's handles are gone). */
export function restoreStructureColliders(p: Physics, handles: readonly number[], eid: number): void {
  for (let i = 0; i < handles.length; i++) {
    if (p.colliderEid.get(handles[i]) === eid) p.colliderEid.set(handles[i], EID_DERELICT)
  }
}
